import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { FormTextInput } from "@redotech/redo-web/text-input";
import { Input, groupInput, input, numberValidator } from "@redotech/ui/form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { memo, useContext } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { TeamContext } from "../../team";

const POLL_FREQUENCY_MS = 3000;

export const orderTrackingBackfillForm = groupInput({
  days: input<string>({ validator: numberValidator({ min: 1, max: 365 }) }),
});

export const OrderTrackingBackfillCard = memo(
  function OrderTrackingBackfillCard({
    daysInput,
  }: {
    daysInput: Input<string>;
  }) {
    const team = useContext(TeamContext);
    const rpcClient = useContext(RedoAdminRpcClientContext);
    const queryClient = useQueryClient();

    // Query for backfill progress with automatic polling
    const { data: progressData } = useQuery({
      queryKey: ["orderTrackingBackfillProgress", team?._id],
      queryFn: () => {
        if (!team || !rpcClient) {
          throw new Error("Team or RPC client not available");
        }
        return rpcClient.getOrderTrackingBackfillProgress({ teamId: team._id });
      },
      enabled: !!team && !!rpcClient,
      refetchInterval: (query) => {
        const data = query.state.data;
        // Poll every 3 seconds if workflow is running, otherwise stop polling
        return data?.found && data?.running ? POLL_FREQUENCY_MS : false;
      },
      refetchOnWindowFocus: false,
    });

    const isRunning = progressData?.found && progressData?.running;
    const isCompleted =
      progressData?.found && !progressData?.running && progressData?.progress;
    const progress = isRunning ? progressData.progress : null;
    const finalStats = isCompleted ? progressData.progress : null;

    // Mutation for triggering backfill
    const triggerBackfillMutation = useMutation({
      mutationFn: async () => {
        if (!team || !rpcClient) {
          throw new Error("Team or RPC client not available");
        }
        const days = parseInt(daysInput.value) || 30;
        return rpcClient.triggerOrderTrackingBackfill({
          teamId: team._id,
          days,
        });
      },
      onSuccess: (response) => {
        if (response.success) {
          toast(
            `Backfill started successfully. Workflow ID: ${response.workflowId}`,
            { variant: "success" },
          );
          // Immediately refetch progress to show the new running workflow
          void queryClient.invalidateQueries({
            queryKey: ["orderTrackingBackfillProgress", team?._id],
          });
        } else {
          toast(response.error || "Unknown error occurred", {
            variant: "error",
          });
        }
      },
      onError: (error) => {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        toast(`Failed to start backfill: ${errorMessage}`, {
          variant: "error",
        });
      },
    });

    if (!team || !rpcClient) {
      return null;
    }

    return (
      <Card title="Order Tracking Backfill">
        <Flex dir="column" gap="lg">
          <Text fontSize="sm">
            Backfill existing orders into Order Tracking v2 by starting
            orderTrackingOrderWorkflow instances for each eligible order.
            Notifications will be suppressed for the first 2 hours.
          </Text>
          <Flex gap="md">
            <FormTextInput
              input={daysInput}
              label="Days to backfill"
              max={365}
              min={1}
              placeholder="30"
              type="number"
            />
            <RedoButton
              disabled={isRunning || triggerBackfillMutation.isPending}
              hierarchy="primary"
              onClick={() => triggerBackfillMutation.mutate()}
              size="sm"
              text={
                isRunning
                  ? "Running..."
                  : triggerBackfillMutation.isPending
                    ? "Starting..."
                    : "Start backfill"
              }
            />
          </Flex>
          {isRunning && progress && (
            <Flex dir="column" gap="sm">
              <Text fontSize="sm" fontWeight="medium">
                Backfill in progress: {progress.progressPercent}
              </Text>
              <Flex dir="column" gap="xs">
                <Text fontSize="sm">Total orders: {progress.totalCount}</Text>
                <Text fontSize="sm">Processed: {progress.totalProcessed}</Text>
                <Text fontSize="sm">
                  Workflows started: {progress.workflowsStarted}
                </Text>
                <Text fontSize="sm">Skipped: {progress.skippedNoShopify}</Text>
                {progress.errors > 0 && (
                  <Text fontSize="sm" textColor="error">
                    Errors: {progress.errors}
                  </Text>
                )}
              </Flex>
            </Flex>
          )}
          {isCompleted && finalStats && (
            <Flex dir="column" gap="sm">
              <Text fontSize="sm" fontWeight="medium" textColor="success">
                Backfill completed successfully!
              </Text>
              <Flex dir="column" gap="xs">
                <Text fontSize="sm">Total orders: {finalStats.totalCount}</Text>
                <Text fontSize="sm">
                  Processed: {finalStats.totalProcessed}
                </Text>
                <Text fontSize="sm">
                  Workflows started: {finalStats.workflowsStarted}
                </Text>
                <Text fontSize="sm">
                  Skipped: {finalStats.skippedNoShopify}
                </Text>
                {finalStats.errors > 0 && (
                  <Text fontSize="sm" textColor="error">
                    Errors: {finalStats.errors}
                  </Text>
                )}
              </Flex>
            </Flex>
          )}
        </Flex>
      </Card>
    );
  },
);
